/**
 * TallyPhone Main Application
 * Original functionality with single-page results display
 */

import { api } from './api.js';
import { registerServiceWorker } from './sw-register.js';

// DOM elements
let searchInput;
let searchButton;
let clearButton;
let searchTypeGroup;
let searchTypeItem;
let suggestionsContainer;
let videoContainer;
let resultsContainer;
let modalOverlay;
let modalBody;
let modalClose;
let mobileSearchIcon;
let mobileSearchOverlay;
let closeSearchBtn;
let desktopSearchInput;
let desktopSearchButton;
let desktopClearButton;
let desktopSuggestionsContainer;

// Debounce timer
let debounceTimer;
let desktopDebounceTimer;

// Track if user selected from dropdown
let selectedFromDropdown = false;
let desktopSelectedFromDropdown = false;

/**
 * Initialize the application
 */
function init() {
  // Get DOM elements
  searchInput = document.getElementById('search-input');
  searchButton = document.getElementById('search-button');
  clearButton = document.getElementById('clear-button');
  searchTypeGroup = document.getElementById('search-type-group');
  searchTypeItem = document.getElementById('search-type-item');
  suggestionsContainer = document.getElementById('suggestions');
  videoContainer = document.getElementById('video-container');
  resultsContainer = document.getElementById('results-container');
  modalOverlay = document.getElementById('modal-overlay');
  modalBody = document.getElementById('modal-body');
  modalClose = document.getElementById('modal-close');

  // Mobile search elements
  mobileSearchIcon = document.getElementById('mobile-search-icon');
  mobileSearchOverlay = document.getElementById('mobile-search-overlay');
  closeSearchBtn = document.getElementById('close-search');

  // Desktop search elements
  desktopSearchInput = document.getElementById('desktop-search-input');
  desktopSearchButton = document.getElementById('desktop-search-button');
  desktopClearButton = document.getElementById('desktop-clear-button');
  desktopSuggestionsContainer = document.getElementById('desktop-suggestions');

  // Mobile search event listeners
  if (mobileSearchIcon) {
    mobileSearchIcon.addEventListener('click', openMobileSearch);
  }
  if (closeSearchBtn) {
    closeSearchBtn.addEventListener('click', closeMobileSearch);
  }

  // Mobile search box event listeners
  searchInput.addEventListener('input', handleInputChange);
  searchInput.addEventListener('keypress', handleKeyPress);
  searchButton.addEventListener('click', redirectToResults);
  clearButton.addEventListener('click', clearSearch);

  // Desktop search box event listeners
  if (desktopSearchInput) {
    desktopSearchInput.addEventListener('input', handleDesktopInputChange);
    desktopSearchInput.addEventListener('keypress', handleDesktopKeyPress);
  }
  if (desktopSearchButton) {
    desktopSearchButton.addEventListener('click', redirectToDesktopResults);
  }
  if (desktopClearButton) {
    desktopClearButton.addEventListener('click', clearDesktopSearch);
  }

  modalClose.addEventListener('click', closeModal);
  modalOverlay.addEventListener('click', handleModalOverlayClick);

  // Close suggestions when clicking outside
  window.addEventListener('click', (e) => {
    const searchBoxes = document.querySelectorAll('.search-box');
    let isOutside = true;
    searchBoxes.forEach(box => {
      if (box.contains(e.target)) {
        isOutside = false;
      }
    });
    if (isOutside) {
      suggestionsContainer.style.display = 'none';
      if (desktopSuggestionsContainer) {
        desktopSuggestionsContainer.style.display = 'none';
      }
    }
  });

  // No URL parameter handling - keep URL clean
}

/**
 * Handle input change for recommendations
 */
function handleInputChange() {
  // Reset selection flag when user types
  selectedFromDropdown = false;

  // Show/hide clear button based on input value
  if (searchInput.value.trim().length > 0) {
    clearButton.style.display = 'flex';
  } else {
    clearButton.style.display = 'none';
  }

  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    fetchRecommendations();
  }, 300);
}

/**
 * Handle Enter key press
 */
function handleKeyPress(event) {
  if (event.key === 'Enter') {
    redirectToResults();
  }
}

/**
 * Activate search box - no longer needed on mobile (search is in header)
 */
function activateSearchBox() {
  // Search box is now in header on mobile, no activation needed
}

/**
 * Deactivate search box - no longer needed
 */
function deactivateSearchBox() {
  // Search box is now in header on mobile, no deactivation needed
}

/**
 * Clear search input and reset UI
 */
function clearSearch() {
  searchInput.value = '';
  selectedFromDropdown = false; // Reset selection flag
  clearButton.style.display = 'none';
  suggestionsContainer.style.display = 'none';
  suggestionsContainer.innerHTML = '';

  // Hide results and show video
  resultsContainer.classList.add('hidden');
  resultsContainer.innerHTML = '';
  videoContainer.classList.remove('hidden');

  // Show title on mobile again
  const title = document.querySelector('h1');
  if (title) {
    title.classList.remove('hide-on-mobile');
  }

  // Deactivate search box
  deactivateSearchBox();

  // Focus back on search input
  searchInput.focus();
}

/**
 * Fetch recommendations from API
 */
async function fetchRecommendations() {
  const query = searchInput.value.trim();
  const type = 'group'; // Always search by group

  suggestionsContainer.innerHTML = '';

  if (query.length < 3) {
    suggestionsContainer.style.display = 'none';
    return;
  }

  try {
    const data = await api.search(type, query);
    const results = data || [];

    if (results.length === 0) {
      suggestionsContainer.style.display = 'none';
      return;
    }

    results.forEach((item) => {
      const suggestionItem = document.createElement('div');
      suggestionItem.classList.add('suggestion-item');
      suggestionItem.textContent = item;
      suggestionItem.addEventListener('click', () => {
        searchInput.value = item;
        selectedFromDropdown = true; // Mark as selected from dropdown
        suggestionsContainer.style.display = 'none';
        redirectToResults();
      });
      suggestionsContainer.appendChild(suggestionItem);
    });

    suggestionsContainer.style.display = 'block';
  } catch (error) {
    console.error('Error fetching suggestions:', error);
    suggestionsContainer.style.display = 'none';
  }
}

/**
 * Show results on same page (silent, no URL change)
 */
function redirectToResults() {
  const query = searchInput.value.trim();

  if (query.length < 3) {
    alert('Please enter at least 3 characters to search.');
    return;
  }

  // Check if user selected from dropdown
  if (!selectedFromDropdown) {
    alert('Please select a product from the dropdown suggestions.');
    return;
  }

  // Hide suggestions
  suggestionsContainer.style.display = 'none';

  // Close mobile search overlay
  closeMobileSearch();

  // Fetch and display results
  fetchAndDisplayResults();
}

/**
 * Fetch and display results
 */
async function fetchAndDisplayResults() {
  const query = searchInput.value.trim();
  const type = 'group'; // Always search by group

  // Hide video, show loading
  videoContainer.classList.add('hidden');
  resultsContainer.innerHTML = '<p style="text-align: center; color: #1e3a5f; font-size: 1.2rem;">Loading...</p>';
  resultsContainer.classList.remove('hidden');

  // Hide title on mobile when showing results
  const title = document.querySelector('h1');
  if (title && window.innerWidth <= 480) {
    title.classList.add('hide-on-mobile');
  }

  // Scroll to results (disabled on mobile)
  if (window.innerWidth > 480) {
    resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  try {
    const data = await api.getSchemeList(type, query);

    if (!data || data.length === 0) {
      resultsContainer.innerHTML = '<p style="text-align: center; color: #2c3e50; font-size: 1.1rem;">No schemes found for your search.</p>';
      return;
    }

    displayResults(data);
  } catch (error) {
    console.error('Error fetching results:', error);
    resultsContainer.innerHTML = '<p style="text-align: center; color: #e74c3c; font-size: 1.1rem;">Failed to load results. Please try again.</p>';
  }
}

/**
 * Display results as cards
 */
function displayResults(schemeItems) {
  const query = searchInput.value.trim();
  const resultCount = schemeItems.length;
  // Get the group name from the first item (all items have the same group)
  const groupName = schemeItems.length > 0 ? schemeItems[0].GROUP : query;

  resultsContainer.innerHTML = '';

  // Add results header
  const resultsHeader = document.createElement('div');
  resultsHeader.classList.add('results-header');
  resultsHeader.innerHTML = `
    <h2 class="results-title">${escapeHtml(groupName)}</h2>
  `;
  resultsContainer.appendChild(resultsHeader);

  // Create cards container
  const cardsContainer = document.createElement('div');
  cardsContainer.classList.add('cards-container');

  schemeItems.forEach((item, index) => {
    const card = document.createElement('div');
    card.classList.add('result-card');

    card.innerHTML = `
      <div class="card-header">
        <div class="card-badge">${escapeHtml(item.ADDITIONAL || 'SCHEME')}</div>
        <div class="card-amount">₹${escapeHtml(item.AMOUNT || 'N/A')}</div>
      </div>
      <div class="card-body">
        <div class="card-info">
          <div class="info-item">
            <span class="info-label">Sales Ledger</span>
            <span class="info-value">${escapeHtml(item.SALESLEDGER || 'N/A')}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Validity</span>
            <span class="info-value">${formatDate(item.FROM)} - ${formatDate(item.TO)}</span>
          </div>
        </div>
      </div>
    `;

    // Card click disabled - no modal popup
    cardsContainer.appendChild(card);
  });

  resultsContainer.appendChild(cardsContainer);

  // Add note at the bottom
  const note = document.createElement('div');
  note.classList.add('results-note');
  note.innerHTML = '<p><strong>Note:</strong> Some Product Details may not be available.</p>';
  resultsContainer.appendChild(note);

  // Scroll to results header after content is rendered (disabled on mobile)
  if (window.innerWidth > 480) {
    setTimeout(() => {
      resultsHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }
}

/**
 * Show modal with full details
 */
function showModal(item) {
  modalBody.innerHTML = `
    <h2>${escapeHtml(item.GROUP || 'Scheme Details')}</h2>
    <p><strong>Sales Ledger:</strong> ${escapeHtml(item.SALESLEDGER || 'N/A')}</p>
    <p><strong>Month:</strong> ${escapeHtml(item.MONTH || 'N/A')}</p>
    <p><strong>Valid From:</strong> ${formatDate(item.FROM)}</p>
    <p><strong>Valid To:</strong> ${formatDate(item.TO)}</p>
    <p><strong>Amount:</strong> ₹${escapeHtml(item.AMOUNT || 'N/A')}</p>
    <p><strong>Company Payout:</strong> ₹${escapeHtml(item.COMPANYPAYOUT || 'N/A')}</p>
    ${item.ADDITIONAL ? `<p><strong>Additional Info:</strong> ${escapeHtml(item.ADDITIONAL)}</p>` : ''}
  `;

  modalOverlay.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
}

/**
 * Close modal
 */
function closeModal() {
  modalOverlay.classList.add('hidden');
  document.body.style.overflow = '';
}

/**
 * Handle modal overlay click (close when clicking outside)
 */
function handleModalOverlayClick(e) {
  if (e.target === modalOverlay) {
    closeModal();
  }
}

/**
 * Open mobile search overlay
 */
function openMobileSearch() {
  if (mobileSearchOverlay) {
    mobileSearchOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    // Focus on search input
    setTimeout(() => {
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }
}

/**
 * Close mobile search overlay
 */
function closeMobileSearch() {
  if (mobileSearchOverlay) {
    mobileSearchOverlay.classList.remove('active');
    document.body.style.overflow = '';
  }
}

/**
 * Desktop search input change handler
 */
function handleDesktopInputChange() {
  desktopSelectedFromDropdown = false;

  if (desktopSearchInput.value.trim().length > 0) {
    desktopClearButton.style.display = 'flex';
  } else {
    desktopClearButton.style.display = 'none';
  }

  clearTimeout(desktopDebounceTimer);
  desktopDebounceTimer = setTimeout(() => {
    fetchDesktopRecommendations();
  }, 300);
}

/**
 * Desktop search key press handler
 */
function handleDesktopKeyPress(event) {
  if (event.key === 'Enter') {
    redirectToDesktopResults();
  }
}

/**
 * Fetch recommendations for desktop search
 */
async function fetchDesktopRecommendations() {
  const query = desktopSearchInput.value.trim();
  const type = 'group';

  desktopSuggestionsContainer.innerHTML = '';

  if (query.length < 3) {
    desktopSuggestionsContainer.style.display = 'none';
    return;
  }

  try {
    const data = await api.search(type, query);
    const results = data || [];

    if (results.length === 0) {
      desktopSuggestionsContainer.style.display = 'none';
      return;
    }

    results.forEach((item) => {
      const suggestionItem = document.createElement('div');
      suggestionItem.classList.add('suggestion-item');
      suggestionItem.textContent = item;
      suggestionItem.addEventListener('click', () => {
        desktopSearchInput.value = item;
        desktopSelectedFromDropdown = true;
        desktopSuggestionsContainer.style.display = 'none';
        redirectToDesktopResults();
      });
      desktopSuggestionsContainer.appendChild(suggestionItem);
    });

    desktopSuggestionsContainer.style.display = 'block';
  } catch (error) {
    console.error('Error fetching suggestions:', error);
    desktopSuggestionsContainer.style.display = 'none';
  }
}

/**
 * Desktop search redirect to results
 */
function redirectToDesktopResults() {
  const query = desktopSearchInput.value.trim();

  if (query.length < 3) {
    alert('Please enter at least 3 characters to search.');
    return;
  }

  if (!desktopSelectedFromDropdown) {
    alert('Please select a product from the dropdown suggestions.');
    return;
  }

  desktopSuggestionsContainer.style.display = 'none';
  fetchAndDisplayDesktopResults();
}

/**
 * Fetch and display desktop results
 */
async function fetchAndDisplayDesktopResults() {
  const query = desktopSearchInput.value.trim();
  const type = 'group';

  videoContainer.classList.add('hidden');
  resultsContainer.innerHTML = '<p style="text-align: center; color: #1e3a5f; font-size: 1.2rem;">Loading...</p>';
  resultsContainer.classList.remove('hidden');

  // Hide title on mobile when showing results
  const title = document.querySelector('h1');
  if (title && window.innerWidth <= 480) {
    title.classList.add('hide-on-mobile');
  }

  // Scroll to results (disabled on mobile)
  if (window.innerWidth > 480) {
    resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  try {
    const data = await api.getSchemeList(type, query);

    if (!data || data.length === 0) {
      resultsContainer.innerHTML = '<p style="text-align: center; color: #2c3e50; font-size: 1.1rem;">No schemes found for your search.</p>';
      return;
    }

    displayResults(data);
  } catch (error) {
    console.error('Error fetching results:', error);
    resultsContainer.innerHTML = '<p style="text-align: center; color: #e74c3c; font-size: 1.1rem;">Failed to load results. Please try again.</p>';
  }
}

/**
 * Clear desktop search
 */
function clearDesktopSearch() {
  desktopSearchInput.value = '';
  desktopSelectedFromDropdown = false;
  desktopClearButton.style.display = 'none';
  desktopSuggestionsContainer.style.display = 'none';
  desktopSuggestionsContainer.innerHTML = '';

  resultsContainer.classList.add('hidden');
  resultsContainer.innerHTML = '';
  videoContainer.classList.remove('hidden');

  // Show title on mobile again
  const title = document.querySelector('h1');
  if (title) {
    title.classList.remove('hide-on-mobile');
  }

  desktopSearchInput.focus();
}

/**
 * Format date from d-m-yyyy format
 */
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  try {
    // Parse d-m-yyyy format (e.g., "1-9-2025")
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS
      const year = parseInt(parts[2], 10);

      const date = new Date(year, month, day);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    return dateString;
  } catch (error) {
    return dateString;
  }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Register service worker
registerServiceWorker();
